package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.KioskClockEvent
import com.evoapps.evoshift.domain.model.KioskClockResult
import com.evoapps.evoshift.domain.model.KioskDevice
import com.evoapps.evoshift.domain.model.KioskPairing
import com.evoapps.evoshift.domain.model.KioskPairingCode
import com.evoapps.evoshift.domain.model.KioskPerson
import com.evoapps.evoshift.domain.model.KioskQrCode
import com.evoapps.evoshift.domain.model.KioskSession
import com.evoapps.evoshift.domain.model.KioskSafeguards
import com.evoapps.evoshift.domain.model.KioskTipPool
import com.evoapps.evoshift.domain.model.Shift
import kotlinx.coroutines.flow.Flow

/**
 * SH-107: the clock-in tablet. Three sides: the tablet itself (session, today's shifts, the team,
 * clocking), the managers (pairing codes, the list of tablets, revoking) and each person (their
 * PIN). Server refusals fail with KioskException, no network with KioskOfflineException.
 */
interface KioskRepository {
    // --- The tablet ---

    /** The tablet session while this device is signed in as a paired tablet, else null. */
    fun observeSession(): Flow<KioskSession?>

    /** Exchanges a manager's one-time [code] for the tablet identity and signs in with it. */
    suspend fun pair(code: String, name: String? = null): Result<KioskPairing>

    /** Signs the tablet out (after [verifyExit]). */
    suspend fun leave(): Result<Unit>

    /** A manager of the workplace confirms leaving kiosk mode with their PIN. */
    suspend fun verifyExit(userId: String, pin: String): Result<Unit>

    /** The workplace's published shifts of these [dates] ("YYYY-MM-DD"), live. */
    fun observeShifts(workplaceId: String, dates: List<String>): Flow<List<Shift>>

    /** The people of the workplace (active engagements), live. */
    fun observeTeam(workplaceId: String): Flow<List<KioskPerson>>

    /** Sends a clock event (kioskClock function). */
    suspend fun clock(event: KioskClockEvent): Result<KioskClockResult>

    /**
     * SH-124: a manager opens the tips on the tablet with their PIN (kioskTipPool, checked like
     * leaving kiosk mode): today's pool (the workplace's today, the server decides) with its split;
     * amountMinor 0 when none was entered yet.
     */
    suspend fun openTips(managerId: String, pin: String): Result<KioskTipPool> =
        Result.failure(UnsupportedOperationException("openTips"))

    /** SH-124: the manager enters (or corrects) today's pool; the server splits it and returns the split. */
    suspend fun saveTips(managerId: String, pin: String, amountMinor: Long): Result<KioskTipPool> =
        Result.failure(UnsupportedOperationException("saveTips"))

    /** SH-111: the next rotating code to show; null when the workplace turned QR clocking off. */
    suspend fun qrCode(): Result<KioskQrCode?>

    // --- Managers ---

    suspend fun createPairingCode(workplaceId: String, name: String?): Result<KioskPairingCode>

    fun observeDevices(workplaceId: String): Flow<List<KioskDevice>>

    suspend fun revoke(workplaceId: String, kioskId: String): Result<Unit>

    // --- The person ---

    /** Whether the signed-in person has a tablet PIN. */
    suspend fun pinStatus(): Result<Boolean>

    /** Sets the person's tablet PIN (exactly 6 digits), or removes it with null. */
    suspend fun setPin(pin: String?): Result<Unit>

    /**
     * SH-111: the signed-in person clocks in or out with a code scanned on the tablet
     * (clockWithQr). The result's displayName is the workplace's name. SH-116: [action] null lets
     * the server decide (end a break going on, else clock out or in); BREAK_START starts a break.
     */
    suspend fun clockWithQr(token: String, action: ClockAction? = null): Result<KioskClockResult>

    // --- SH-111: the workplace's QR switch (settings.kioskQrEnabled, default on) ---

    fun observeQrEnabled(workplaceId: String): Flow<Boolean>

    suspend fun setQrEnabled(workplaceId: String, enabled: Boolean): Result<Unit>

    /** SH-134: the workplace's tablet safeguards (QR only, the clock-in photo), live. */
    fun observeSafeguards(workplaceId: String): Flow<KioskSafeguards> = kotlinx.coroutines.flow.flowOf(KioskSafeguards())
}
